import { Tooltip, Typography, TypographyProps, styled } from '@mui/material'
import { FC, useEffect, useRef, useState } from 'react'

export interface IDynamicTextTypographyProps extends TypographyProps {
  text: string
  maxLength?: number
  overWriteText?: string
  isTooltipDisabled?: boolean
}

const StyledDiv = styled('span')(() => ({
  cursor: 'pointer',
  fontSize: 'inherit',
}))

export const DynamicTextTypography: FC<IDynamicTextTypographyProps> = ({
  text,
  maxLength,
  overWriteText = '...',
  children,
  isTooltipDisabled = false,
  ...rest
}) => {
  const exceedsMaxLength = maxLength != null && text?.length > maxLength
  // Only truncate + append the ellipsis when the hidden remainder is more than one word. If a single
  // word is all that remains, show the full text instead of cutting it and adding a misleading "...".
  const remainder = exceedsMaxLength ? text.slice(maxLength).trim() : ''
  const remainderWordCount = remainder ? remainder.length : 0
  const shouldTruncate = exceedsMaxLength && remainderWordCount > 1
  const truncatedText = shouldTruncate ? text.slice(0, maxLength) + overWriteText : text

  return (
    <Typography {...rest} display="flex" flexDirection="row">
      {shouldTruncate ? (
        <Tooltip title={text} disableHoverListener={isTooltipDisabled}>
          <StyledDiv>{truncatedText}</StyledDiv>
        </Tooltip>
      ) : (
        text
      )}
      {children}
    </Typography>
  )
}

export const DynamicTypography: FC<IDynamicTextTypographyProps> = ({
  text,
  overWriteText = '...',
  children,
  isTooltipDisabled = false,
  ...rest
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [shouldTruncate, setShouldTruncate] = useState(false)

  useEffect(() => {
    if (ref.current) {
      const e = ref.current
      setShouldTruncate(e.offsetWidth < e.scrollWidth)
    }
  }, [])

  return (
    <Typography {...rest} display="flex" flexDirection="row" textAlign={'end'}>
      <Tooltip title={text} disableHoverListener={!shouldTruncate || isTooltipDisabled}>
        <StyledDiv ref={ref} style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
          {text}
        </StyledDiv>
      </Tooltip>
      {children}
    </Typography>
  )
}
